import { useState, useEffect } from "react";
import { createJobStatusUrl } from "../admin/dashboards/urls";
import { JobStatusResponse } from "../admin/dashboards/types";
import { useQuery } from "wasp/client/operations";
import { getEnrichmentTask } from "wasp/client/operations";
import { EnrichmentJob } from "wasp/entities";

type UseJobProgressProps = {
  initialProgress: number;
  jobId: string;
  onCompleted?: (res: any) => void;
  onError?: (error: string) => void;
  job?: EnrichmentJob;
}

type UseJobProgressHook = {
  progress: number;
  error: string | null;
  completed: boolean;
  message: string | null;
}


export const useJobProgress = ({jobId, onCompleted, onError, initialProgress, job} : UseJobProgressProps) : UseJobProgressHook => {

  if (job) {
    console.warn('useJobProgress is using direct job object data instead of API. Job progress is managed by backend (checkExportJobStatus)');
  }
  
  const [progress, setProgress] = useState(job?.progress || initialProgress);
  const [error, setError] = useState<string | null>(null);
  const [completed, setCompleted] = useState(job?.status === 'completed');
  const [message, setMessage] = useState<string|null>(job?.failReason || null);

  useEffect(() => {
    if (job) {
      if (job.status === 'completed' && !completed) {
        setCompleted(true);
        setProgress(100);
        onCompleted && onCompleted(null);
      }
      
      if (job.status === 'failed' && !error) {
        setError(job.failReason || 'Job failed');
        onError && onError(job.failReason || 'Job failed');
      }
      
      if (job.progress !== undefined && job.progress !== progress) {
        setProgress(job.progress || 0);
      }
      
      if (job.failReason !== message) {
        setMessage(job.failReason || null);
      }
    }
  }, [job, completed, error, progress, message]);

  useEffect(() => {
    if (!jobId || job) return; 
    
    const fetchJobProgress = async () => {
      try {
        const response = await fetch(createJobStatusUrl(jobId));
        const data = await response.json() as JobStatusResponse;

        if (data.status === "completed") {
          setProgress(100);
          setCompleted(true);
          onCompleted && onCompleted(data.response);
        } else if (data.status === "in_progress") {
            setMessage(data.message);
            setProgress(prev => data.progress > prev ? data.progress : prev);
        } else if (data.status === "pending") {
          setMessage(data.message);
        } else {
          setError(data.message)
          // alert(data.message)
          onError && onError(data.message);
        }
      } catch (error) {
        setError("Failed to fetch job progress");
      }
    };

    const interval = setInterval(fetchJobProgress, 5000);
    fetchJobProgress();

    return () => clearInterval(interval);
  }, [jobId, job]);
  
  if(!jobId && !job){
    return {
      progress: 0,
      error: "Job ID and job object are missing",
      completed: false,
      message
    }
  }
  
  return {
    progress: progress,
    error: error,
    completed: completed,
    message
  }
}